import React, { useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";

import "./ImportFuelRegistry.scss";

import {
	Button,
	AttachmentsUpload,
	Table,
} from "abs-ui-components";

import axiosHttp from "../../services/interceptor";
import { emitToasterMsg } from "../../services/toasterUtility";
import xlsxImage from "../../images/excel-file-icon.svg";

const baseURL = process.env.REACT_APP_BASE_URL;

const readField = (obj, ...keys) => {
	for (const key of keys) {
		if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
			return obj[key];
		}
	}
	return undefined;
};

const downloadBase64File = (
	base64Content,
	fileName = "FuelRegistryImportErrors.xlsx",
	mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
) => {
	if (!base64Content) return;

	try {
		const byteCharacters = window.atob(base64Content);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: mimeType });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Error report download failed:", error);
	}
};

const ImportFuelRegistry = ({ cancelImportFuelRegistry }) => {
	const [files, setFiles] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [dragged, setDragged] = useState(null);
	const [uploadErrors, setUploadErrors] = useState([]);
	const [apiErrorMessage, setApiErrorMessage] = useState("");
	const [apiErrorFile, setApiErrorFile] = useState(null);
	const fileInputRef = useRef(null);

	const cancelFileSelection = () => {
		setFiles([]);
		setUploadErrors([]);
		setApiErrorMessage("");
		setApiErrorFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleRemoveFile = (index) => {
		setFiles((prev) => prev.filter((_, idx) => idx !== index));
	};

	const fileSelectionHandler = (selectedFilesList) => {
		const selectedFile = selectedFilesList?.[0]?.file;
		if (!selectedFile) return;
		setUploadErrors([]);
		setFiles([selectedFile]);
	};

	const invalidFuelRegistryColumns = [
		{ Header: "File Name", accessor: "fileName" },
		{
			Header: "Message",
			accessor: "message",
			Cell: ({ row }) => (
				<span className="text-danger" style={{ whiteSpace: "pre-line" }}>
					{row.message}
				</span>
			),
		},
	];

	const handleUpload = async () => {
		if (!files.length) {
			return;
		}

		setUploading(true);
		const formData = new FormData();
		files.forEach((file) => {
			formData.append("file", file);
		});

		try {
			const response = await axiosHttp.post(
				`${baseURL}api/MyFleet/ImportFuelRegistries`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);

			const result = response?.data || {};
			const rowErrorReport =
				readField(result, "rowErrorReport", "RowErrorReport") ||
				readField(result, "rowErrorReportBase64", "RowErrorReportBase64");
			const errorMessage =
				readField(result, "errorMessage", "ErrorMessage") ||
				readField(result, "message", "Message") ||
				readField(result, "title", "Title") ||
				"";
			const fileName =
				readField(result, "fileName", "FileName") ||
				files[0]?.name ||
				"FuelRegistryImportErrors.xlsx";
			const importFuelRegistryRowResponses =
				readField(result, "importFuelRegistryRowResponses", "ImportFuelRegistryRowResponses") ||
				readField(result, "importFuelRegistryRowRes", "ImportFuelRegistryRowRes") ||
				[];

			if (rowErrorReport && (result?.isFileValid === false || result?.IsFileValid === false)) {
				const rowErrorMessage =
					errorMessage || "Import failed. Please review the error report.";
				setUploadErrors([
					{
						fileName: fileName || files[0]?.name || "Fuel Registry File",
						message: (
							<>
								<span style={{ color: "#dc1e34" }}>{rowErrorMessage}</span>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										downloadBase64File(rowErrorReport);
									}}
									style={{ marginLeft: "8px", color: "#dc1e34", textDecoration: "underline" }}
								>
									Download error report
								</a>
							</>
						),
					},
				]);
				setApiErrorMessage("");
				setApiErrorFile(null);
				return;
			}

			if (result?.isFileValid === false || result?.IsFileValid === false) {
				const rows =
					importFuelRegistryRowResponses.length > 0
						? importFuelRegistryRowResponses
							.map((row) => {
								const message =
									readField(row, "errorMessage", "ErrorMessage") ||
									readField(row, "message", "Message") ||
									readField(row, "rowErrorMessage", "RowErrorMessage") ||
									(readField(row, "errors", "Errors") || []).join(", ");
								return message
									? { fileName: fileName || files[0]?.name || "Fuel Registry File", message }
									: null;
							})
							.filter(Boolean)
					: [{ fileName: fileName || files[0]?.name || "Fuel Registry File", message: errorMessage || "Unable to upload the fuel registry file. Please try again." }];

				setUploadErrors(rows);
				setApiErrorMessage(rows.length > 0 ? "" : (errorMessage || "Unable to upload the fuel registry file. Please try again."));
				setApiErrorFile(null);
				return;
			}

			if (errorMessage) {
				const rows = [{ fileName: fileName || files[0]?.name || "Fuel Registry File", message: errorMessage }];
				setUploadErrors(rows);
				setApiErrorMessage("");
				setApiErrorFile(null);
				return;
			}

			setApiErrorMessage("");
			setApiErrorFile(null);
			emitToasterMsg(
				"success",
				"Import Fuel Registry",
				readField(result, "successMessage", "SuccessMessage") ||
					"Imported successfully"
			);
			cancelImportFuelRegistry?.(true);
		} catch (error) {
			const response = error?.response;
			const contentType = response?.headers?.["content-type"] || "";
			const payload = response?.data;

			let errorMessage =
				readField(payload, "errorMessage", "ErrorMessage") ||
				readField(payload, "title", "Title") ||
				readField(payload, "message", "Message") ||
				"Unable to upload the fuel registry file. Please try again.";

			if (typeof payload === "string" && payload.trim()) {
				errorMessage = payload;
			}

			if (contentType.includes("application/json") && payload?.errors) {
				errorMessage =
					payload.errors?.file?.[0] ||
					payload.errors?.files?.[0] ||
					errorMessage;
			}

			if (payload instanceof Blob) {
				const text = await payload.text();
				errorMessage = text || errorMessage;
			}

			setUploadErrors([
				{
					fileName: files[0]?.name || "Fuel Registry File",
					message: errorMessage,
				},
			]);
			setApiErrorMessage(errorMessage);
			setApiErrorFile(null);
		} finally {
			setUploading(false);
		}
	};

	const onDrag = (e) => {
		e.preventDefault();
		if (e.type === "dragover") {
			setDragged(true);
		}
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragged(false);
	};

	const onDraggingEnd = (e) => {
		e.preventDefault();
		setDragged(false);
	};

	return (
		<div
			className="import-fuel-registry-wrapper"
			onDragLeave={(e) => onDraggingEnd(e)}
		>
			<div className="selected-content-container mb-4">
				<div
					onDragOver={(e) => onDrag(e)}
					onDrop={(e) => onDrop(e)}
					className={dragged ? "drag-drop-cls mt-4 ml-4 mr-4" : "mt-4 ml-4 mr-4"}
				>
					<AttachmentsUpload
						onFilesSelected={(e) => fileSelectionHandler(e)}
						supportedFormat="excel"
						supportedFileTypes="xlsx"
					/>

					<div className="selected-content mt-4">
						{files.length > 0 && (
							<>
								<div>Uploaded Files:</div>
								<div className="mt-3">
									{files.map((file, index) => (
										<div
											key={`import-fuel-registry-${index}`}
											className="d-flex justify-content-between align-items-center px-3 py-1"
										>
											<div className="d-flex">
												<img
													src={xlsxImage}
													alt="Excel File Icon"
													className="xl-img mt-1"
												/>
												<span className="file-name p-2">{file?.name}</span>
											</div>
											<button
												type="button"
												className="close p-0"
												aria-label="Close"
												onClick={() => handleRemoveFile(index)}
											>
												<span aria-hidden="true">×</span>
											</button>
										</div>
									))}
								</div>
								<div className="mt-4 d-flex upload-container">
									{uploading && <CircularProgress size={30} />}
									<Button
										onClick={handleUpload}
										className="ml-2"
										disabled={uploading}
									>
										Upload
									</Button>
									<Button
										onClick={cancelFileSelection}
										className="ml-2"
									>
										Clear
									</Button>
								</div>
							</>
						)}
						{uploadErrors.length > 0 && (
							<>
								<div className="mt-3">One or More errors found as below :</div>
								<Table
									columns={invalidFuelRegistryColumns}
									data={uploadErrors}
								/>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ImportFuelRegistry;
